import { useCallback, useEffect, useRef } from 'react';
import type { FormSubmitHandler } from 'react-hook-form';
import { Controller, useForm } from 'react-hook-form';

import { useTranslations } from '~i18n/hooks';
import { ButtonPrimary, InputField } from '~shared/ui/molecules';

import { CheckboxRow } from '../CheckboxRow';
import {
	ButtonSection,
	FixedSection,
	FormStyled,
	InputSection,
	Options,
} from './styled';

type IProps = {
	onSubmit: (val: string) => void;
};

type TOption = {
	value: string;
	text: string;
};

export type TReasonsFormValues = {
	[key: string]: boolean;
} & {
	otherReason?: string;
};

export const ReasonsForm = ({ onSubmit }: IProps) => {
	const t = useTranslations('MySubscriptions');
	const rawOptions: unknown = t.raw('reasons');
	const options: TOption[] = Array.isArray(rawOptions)
		? (rawOptions as TOption[])
		: [];

	return (
		<PureForm
			onSubmit={onSubmit}
			options={options}
			buttonLabel={t('reasonsButton')}
		/>
	);
};

// TODO: improve validation, add i18n to validation error
export const PureForm = ({
	onSubmit: onSuccess,
	options,
	buttonLabel,
}: IProps & { options: TOption[]; buttonLabel: string }) => {
	const scrollContainerRef = useRef<HTMLDivElement>(null);
	const {
		control,
		register,
		watch,
		formState: { isSubmitting, isValid, errors },
	} = useForm<TReasonsFormValues>({
		mode: 'onChange',
		reValidateMode: 'onChange',
		defaultValues: options.reduce((acc, option) => {
			acc[option.value] = false;
			return acc;
		}, {} as TReasonsFormValues),
	});

	const lastOptionValue = options[options.length - 1]?.value;
	const isLastOptionChecked = watch(lastOptionValue) || false;

	const validateAtLeastOne = (_: boolean, formValues: TReasonsFormValues) => {
		const hasSelected = Object.entries(formValues).some(
			([key, val]) => val === true && key !== 'otherReason'
		);
		return hasSelected || 'Required';
	};

	const validateOtherReason = (
		value: string | undefined,
		formValues: TReasonsFormValues
	) => {
		if (formValues[lastOptionValue] && !value?.trim()) {
			return 'Required';
		}
		return true;
	};

	const onSubmit: FormSubmitHandler<TReasonsFormValues> = useCallback(
		({ data }) => {
			const selectedReasons = Object.entries(data)
				.filter(([key, value]) => value === true && key !== 'otherReason')
				.map(([key]) => key);

			if (data[lastOptionValue] && data.otherReason) {
				selectedReasons.push(data.otherReason);
			}

			const result = selectedReasons.join(';');
			onSuccess(result);
		},
		[onSuccess, lastOptionValue]
	);

	useEffect(() => {
		if (isLastOptionChecked && scrollContainerRef.current) {
			const container = scrollContainerRef.current;

			container.scrollTo({
				top: container.scrollHeight,
				behavior: 'smooth',
			});
		}
	}, [isLastOptionChecked]);

	return (
		<FormStyled control={control} onSubmit={onSubmit}>
			<Options ref={scrollContainerRef}>
				{options.map(({ value, text }) => (
					<Controller
						key={value}
						name={value}
						control={control}
						rules={{ validate: validateAtLeastOne }}
						render={({ field: { onChange, value: fieldValue, name } }) => (
							<CheckboxRow
								checked={fieldValue}
								onChange={(e) => onChange(e.target.checked)}
								name={name}
							>
								{text}
							</CheckboxRow>
						)}
					/>
				))}
			</Options>

			<FixedSection>
				{isLastOptionChecked && (
					<InputSection>
						<InputField
							{...register('otherReason', {
								validate: validateOtherReason,
								maxLength: 500,
								minLength: 1,
							})}
							placeholder="Your comment"
							error={errors.otherReason?.message}
						/>
					</InputSection>
				)}

				<ButtonSection>
					<ButtonPrimary
						type="submit"
						disabled={!isValid || isSubmitting}
						loading={isSubmitting}
						size="M"
						fullWidth
					>
						{buttonLabel}
					</ButtonPrimary>
				</ButtonSection>
			</FixedSection>
		</FormStyled>
	);
};
